import { AppConfig } from '@cco-apps/cco-frontend';

/**
 * The runtime config could not be read, so the app cannot boot. Distinct from a
 * failure inside bootstrapping itself: this one is usually the edge auth gate
 * turning the request into a redirect, and is recoverable by signing in again.
 */
export class ConfigUnavailableError extends Error {
  // Set explicitly rather than through `super(message, { cause })`: this project
  // predates the ES2022 lib that declares the two-argument Error constructor.
  readonly cause?: unknown;

  constructor(url: string, reason: string, cause?: unknown) {
    super(`Could not load the application config from ${url}: ${reason}`);
    this.name = 'ConfigUnavailableError';
    this.cause = cause;
  }
}

/**
 * Fetches and parses the runtime config, reporting every way it can fail as a
 * ConfigUnavailableError.
 *
 * The status check is load-bearing. When the Angular service worker's own fetch
 * fails it answers with a synthetic `504 Gateway Timeout` carrying an empty body;
 * going straight to `.json()` would surface that as an unexplained syntax error
 * and leave the boot splash spinning forever.
 */
export async function loadAppConfig(url: string): Promise<AppConfig> {
  let response: Response;

  try {
    response = await fetch(url);
  } catch (cause) {
    throw new ConfigUnavailableError(url, 'the request could not be completed', cause);
  }

  if (!response.ok) {
    throw new ConfigUnavailableError(url, `the server answered ${response.status} ${response.statusText}`);
  }

  try {
    return await response.json() as AppConfig;
  } catch (cause) {
    throw new ConfigUnavailableError(url, 'the response was not valid JSON', cause);
  }
}
